import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { AspectRatio, Box, Divider, Flex, Image, SlideFade, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { default as BoxComponent } from "../../../components/Box";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import ImporterForm from "../../forms/Importer";
import useGetOrganization from "../../../api/useGetOrganization";
import useEntitySelection from "../../../hooks/useEntitySelection";
import { typedSxMap } from "../../../utils/typedSxMap";
import imageCreate from "../../../assets/screenshots/create.png";
import imageEmbed from "../../../assets/screenshots/embed.png";
import imageRetrieve from "../../../assets/screenshots/retrieve.png";
import { FiEdit } from "react-icons/fi";
import { GoCodeSquare } from "react-icons/go";
import { TbCloudDataConnection } from "react-icons/tb";

const SliderShow = () => {
  const { data: organization } = useGetOrganization();
  const workspaceId = organization?.workspaces?.[0]?.id || "";
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const styles = {
    bgColor: useColorModeValue("#F9F5FF", "#1D2939"),
    iconBg: useColorModeValue("#F4EBFF", "#475467"),
    iconColor: useColorModeValue("#7A5EF8", "#FFFFFF"),
    borderColor: useColorModeValue("#EAECF0", "#344054"),
    textColor: useColorModeValue("#344054", "#D0D5DD"),
  };

  const sxStyles = typedSxMap({
    stackWrapper: {
      flexDirection: { base: "column", sm: "column", md: "column", lg: "row", xl: "row", "2xl": "row" },
    },
  });

  const steps = [
    {
      imageUrl: imageCreate,
      icon: <FiEdit style={{ color: styles.iconColor }} />,
      step: 1,
      title: "Create ",
      subtitle: "Create a new importer and add columns",
    },
    {
      imageUrl: imageEmbed,
      icon: <GoCodeSquare style={{ color: styles.iconColor }} />,
      step: 2,
      title: "Embed",
      subtitle: "Embed the importer into your application",
    },
    {
      imageUrl: imageRetrieve,
      icon: <TbCloudDataConnection style={{ color: styles.iconColor }} />,
      step: 3,
      title: "Retrieve",
      subtitle: "Retrieve the imported data via onComplete or webhooks",
    },
  ];

  const handleMenuClick = (index: number) => {
    setCurrentSlide(index);
  };

  const { modal } = useEntitySelection();

  const modalContent = useMemo(
    () => (
      <BoxComponent variants={["wide", "space-mid"]}>
        <ImporterForm
          title="Create Importer"
          buttonLabel="Add"
          onSuccess={(data) => {
            if (data?.data?.id) {
              navigate(`/importers/${data?.data?.id}/template`);
            } else {
              navigate("/importers");
            }
          }}
          context={{ workspaceId }}
        />
      </BoxComponent>
    ),
    [modal.openDelayed]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % steps.length);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [currentSlide, steps.length]);

  return (
    <>
      <Divider mt={4} mb={4} borderColor={useColorModeValue("#EAECF0", "#344054")} borderStyle={"solid"} paddingTop={"20px"} />

      <Stack
        sx={sxStyles.stackWrapper}
        justifyContent={{ base: "flex-start", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        flex={1}
        mb={2}>
        <Flex align="center">
          <Box>
            <Text fontSize={"30px"} mb={"0 !important"} lineHeight={"38px"} fontWeight={600}>
              Build your importer
            </Text>
            <Text as={"p"} lineHeight={"24px"} fontWeight={400} color={useColorModeValue("#344054", "#98A2B3")} fontSize={"14px"}>
              In three steps
            </Text>
          </Box>
        </Flex>
        <Button variants={["primary"]} onClick={() => modal.setOpen(true)}>
          Create a New Importer
        </Button>
      </Stack>
      <Stack direction={{ base: "column", md: "row" }} justifyContent="center" alignItems="center">
        <Flex flex={{ base: 1, md: 1 }} direction="column" mr={{ base: 0, md: 4 }} gap={5}>
          {steps.map((step, index) => (
            <Box
              key={index}
              onClick={() => handleMenuClick(index)}
              cursor="pointer"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              border="1px solid"
              borderColor={currentSlide === index ? "#7A5EF8" : styles.borderColor}
              backgroundColor={currentSlide === index ? styles.bgColor : ""}
              borderRadius={10}
              height="100%"
              transition="background-color 0.3s ease, border-color 0.3s ease">
              <Flex alignItems={"center"}>
                <Box
                  backgroundColor={styles.iconBg}
                  borderRadius="50%"
                  padding={1}
                  justifyContent={"center"}
                  alignItems={"center"}
                  display={"flex"}
                  height={"32px"}
                  width={"32px"}>
                  {step.icon}
                </Box>

                <Box ml={2}>
                  <Text fontSize="16px" mb={"0 !important"} fontWeight={400} color="var(--color-text)">
                    Step {step.step}:{" "}
                    <Text as={"span"} fontWeight={500} color={styles.textColor}>
                      {step.title}
                    </Text>
                  </Text>
                  <Text as="p" fontWeight={400} color={styles.textColor} fontSize="14px">
                    {step.subtitle}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ))}
        </Flex>


          <SlideFade in={true} offsetY="-20px">
              <Image
                src={steps[currentSlide].imageUrl}
                alt={`${steps[currentSlide].title} ${steps[currentSlide].subtitle}`}
                borderRadius="md"
                width={{ base: "530px",sm: "350px", md: "350px", lg:"400px", xl: "530px", "2xl": "530px" }}
                height={{base: "350px",sm: "270px", md: "270px", lg: "300px", xl: "350px", "2xl": "350px"}}
                transition="opacity 0.3s ease 0s"
              />
          </SlideFade>
      </Stack>

      {modal.openDelayed && (
        <Modal {...modal} useBox={false} useCloseButton>
          {modalContent}
        </Modal>
      )}
    </>
  );
};

export default SliderShow;
